import React, { useState, useEffect, memo, useRef } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import {
    List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Collapse, Tooltip, styled,
    Menu,
    Box,
} from '@mui/material';
import {
    ExpandLess, ExpandMore,
    Assignment,
    Payment,
    Circle as CircleIcon,
    People as PeopleIcon,
    Receipt as ReceiptIcon,
    Sync as SyncIcon,
    Assessment as AssessmentIcon,
    Settings as SettingsIcon,
    AdminPanelSettings as AdminIcon,
    Chat as ChatIcon,
    History as HistoryIcon,
    AccountTree as AccountTreeIcon
} from '@mui/icons-material';

const StyledListItemButton = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'drawerOpen' && prop !== 'isSubItem',
})(({ theme, isActive, drawerOpen, isSubItem }) => ({
    minHeight: 48,
    justifyContent: drawerOpen ? 'initial' : 'center',
    paddingLeft: isSubItem ? theme.spacing(4) : theme.spacing(2),
    fontFamily:'Lato !important',
    fontSize:14,
    backgroundColor: isActive ? theme.palette.navItems.active : theme.palette.navItems.in_active,
    color: isActive ? theme.palette?.navItems?.active_text : theme.palette?.navItems?.in_active_text,
    '&:hover': {
        backgroundColor: isActive ? theme.palette.navItems.active : theme.palette.navItems.hover,
        color: isActive ? theme.palette?.navItems?.active_text : theme.palette.grey.A200
    },
}));

const StyledSubListItemButton = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'drawerOpen' && prop !== 'isSubItem',
})(({ theme, isActive, drawerOpen, isSubItem }) => ({
    minHeight: 48,
    justifyContent: drawerOpen ? 'initial' : 'center',
    fontFamily:'Lato !important',
    paddingLeft: isSubItem ? theme.spacing(4) : theme.spacing(2),
    backgroundColor: isActive ? theme.palette.navItems.active : theme.palette.navItems.sub_menu_in_active,
    color: isActive ? theme.palette?.navItems?.active_text : theme.palette?.navItems?.sub_menu_in_active_text,
    '&:hover': {
        backgroundColor: isActive ? theme.palette.navItems.active : theme.palette.navItems.hover,
        color: isActive ? theme.palette?.navItems?.active_text : theme.palette.grey.A200
    },
}));



const StyledListItemIcon = styled(ListItemIcon, {
    shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'drawerOpen',
})(({ theme, isActive, drawerOpen }) => ({
    minWidth: 0,
    marginRight: drawerOpen ? theme.spacing(2) : 'auto',
    color: isActive ? theme.palette.grey[0] : 'inherit',
}));

const FlyoutMenu = styled(Menu)({
    pointerEvents: 'none',
    '& .MuiPaper-root': {
        pointerEvents: 'auto',
        marginLeft: '4px',
        width: '220px',
        borderRadius: '8px',
        boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
        border: 'none',
        padding: '0',
    }
});

const FlyoutHeader = styled(Box)({
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f1f1f',
    borderBottom: '1px solid #e0e0e0',
});

const NavItems = ({ drawerOpen }) => {
    const history = useHistory();
    const location = useLocation();
    const timeoutRef = useRef(null);

    const [moduleMenuSet, setModuleMenuSet] = useState([]);
    const [openMenus, setOpenMenus] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const [hoveredPage, setHoveredPage] = useState(null);

    useEffect(() => {
        const isAdmin = JSON.parse(localStorage.getItem("isAdminModule"));
        const menuData = isAdmin
            ? JSON.parse(localStorage.getItem('adminModuleMenuSet') || "[]")
            : JSON.parse(localStorage.getItem('practiceModuleMenuSet') || "[]");
        setModuleMenuSet(menuData);
    }, []);

    const handleParentClick = (name) => {
        if (!drawerOpen) return;
        setOpenMenus(prev => ({ ...prev, [name]: !prev[name] }));
    };

    const handleRoute = (route) => {
        if (location.pathname !== route) history.push(route);
        handleClose();
    };

    const handleMouseEnter = (event, page) => {
        if (!drawerOpen && page?.sub_menu?.length > 0) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setAnchorEl(event.currentTarget);
            setHoveredPage(page);
        }
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setAnchorEl(null);
            setHoveredPage(null);
        }, 100);
    };

    const handleMenuEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setHoveredPage(null);
    };

    const getIcons = (name) => {
        switch (name) {
            case "Demographics": return <PeopleIcon />;
            case "Charges": return <ReceiptIcon />;
            case "Interface": return <SyncIcon />;
            case "Reimbursement": return <Payment />;
            case "Reports": return <AssessmentIcon />;
            case "Account Setup": return <AccountTreeIcon />;
            case "Administration": return <AdminIcon />;
            case "Settings": return <SettingsIcon />;
            case "Chat": return <ChatIcon />;
            case "Assignments": return <Assignment />;
            case "Audit Logs": return <HistoryIcon />;
            case "Claim Beta": return <ReceiptIcon />;
            default: return <CircleIcon sx={{ fontSize: '12px' }} />;
        }
    };

    return (
        <List sx={{ px: 0 }}>
            {moduleMenuSet.map((page) => {
                const hasSubMenu = page?.sub_menu?.length > 0;
                const isExpanded = openMenus[page?.name];
                const isActive = (location?.pathname === page?.route) || 
                 (page?.sub_menu || []).some(sm => sm && sm.route === location?.pathname);

                return (
                    <React.Fragment key={page.name}>
                        <Tooltip title={(!drawerOpen && !hasSubMenu) ? page.name : ""} placement="right">
                            <ListItem 
                                disablePadding 
                                sx={{ display: 'block' }} 
                                onMouseEnter={(e) => handleMouseEnter(e, page)}
                                onMouseLeave={handleMouseLeave}
                            >
                                <StyledListItemButton
                                    isActive={isActive}
                                    drawerOpen={drawerOpen}
                                    onClick={() => (hasSubMenu && drawerOpen) ? handleParentClick(page.name) : handleRoute(page.route)}
                                >
                                    <StyledListItemIcon isActive={isActive} drawerOpen={drawerOpen}>
                                        {getIcons(page?.name)}
                                    </StyledListItemIcon>

                                    {drawerOpen && (
                                        <>
                                            <ListItemText
                                                primary={page.name}
                                                primaryTypographyProps={{
                                                    fontSize: '14px !important',
                                                    fontWeight: isActive ? '600 !important': '400 !important'
                                                }}
                                            />
                                            {hasSubMenu && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
                                        </>
                                    )}
                                </StyledListItemButton>
                            </ListItem>
                        </Tooltip>

                        {hasSubMenu && drawerOpen && (
                            <Collapse in={isExpanded && drawerOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {page?.sub_menu?.map((subItem) => {
                                        const isSubActive = location?.pathname === subItem?.route;
                                        return (
                                            <StyledSubListItemButton
                                                key={subItem?.name}
                                                isSubItem
                                                isActive={isSubActive}
                                                drawerOpen={drawerOpen}
                                                onClick={() => handleRoute(subItem?.route)}
                                            >
                                                <ListItemIcon sx={{ minWidth: 0, mr: 2, color: 'inherit' }}>
                                                    <CircleIcon sx={{ fontSize: '6px' }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={subItem?.name}
                                                    primaryTypographyProps={{ fontSize: '13px' }}
                                                />
                                            </StyledSubListItemButton>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        )}
                    </React.Fragment>
                );
            })}

            <FlyoutMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && !drawerOpen}
                onClose={handleClose}
                hideBackdrop
                disableEnforceFocus
                disableRestoreFocus
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onMouseEnter={handleMenuEnter}
                onMouseLeave={handleMouseLeave}
                sx={{ mt: -1 }}
            >
                {hoveredPage && (
                    <Box>
                        <FlyoutHeader>{hoveredPage.name}</FlyoutHeader>
                        <List disablePadding>
                            {hoveredPage.sub_menu.map((sub) => {
                                const isSubActive = location.pathname === sub.route;
                                return (
                                    <StyledSubListItemButton
                                        key={sub.name}
                                        isActive={isSubActive}
                                        onClick={() => handleRoute(sub.route)}
                                        sx={{ py: 1 }}
                                    >
                                        <ListItemText 
                                            primary={sub.name} 
                                            primaryTypographyProps={{ fontSize: '13px' }} 
                                        />
                                    </StyledSubListItemButton>
                                );
                            })}
                        </List>
                    </Box>
                )}
            </FlyoutMenu>
        </List>
    );
}

export default memo(NavItems);